/**
 * Custom fetch query that re-authorizes using the refresh token
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
 */
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { fetchBaseQuery, retry } from '@reduxjs/toolkit/query'
import type { Alert } from '@tunasong/models'
import { isOnline, isTest, logger, networkBus } from '@tunasong/models'
import invariant from 'tiny-invariant'
import { isPermanentAuthError, OAuth } from '../auth/index.js'
import { notificationsSlice } from '../features/notifications/notifications-slice.js'
import { userSlice } from '../features/user/user-slice.js'
import { loginProcessMutex } from './auth-mutex.js'

const MAX_RETRIES = 1

/**
 * Sentinel distinguishing "refresh failed, but retrying could still work" from
 * "there is no session". Both yield no AuthData, but only the latter may log the
 * user out — discarding a live session on a network blip is the GH #1469
 * regression.
 */
const transientRefreshFailure = Symbol('transient-refresh-failure')

/**
 * `OAuth.getSession()` REJECTS on a refresh failure (`AuthTokenError`). The
 * refresh block below had only a `finally`, so that rejection escaped
 * `baseQueryWithReauth` altogether: the query rejected, `OAuth.logout()` never
 * ran, and AuthGuard never saw it — it wraps its own `getSession()` call, not
 * this one. In normal app usage this is the only caller, so a permanently dead
 * refresh token (Cognito `invalid_grant`, after which the auth-proxy clears the
 * cookie) left the user stuck on a session that could never recover.
 *
 * Permanent → resolve to `null` so the caller re-authenticates.
 * Transient → resolve to the sentinel so the session is left untouched.
 */
const getSessionForReauth = async () => {
  try {
    return await OAuth.getSession()
  } catch (error) {
    if (isPermanentAuthError(error)) {
      logger.warn('Token refresh permanently rejected; re-authenticating', error)
      return null
    }
    logger.warn('Token refresh failed transiently; keeping the session (GH #1469)', error)
    return transientRefreshFailure
  }
}

/** @note these must be allowed by CORS for local dev */
export const RTK_QUERY_ARGS_HEADER = 'x-rtk-query-args'
export const RTK_QUERY_API_NAME_HEADER = 'x-rtk-query-api-name'
export const RTK_QUERY_ENDPOINT_NAME_HEADER = 'x-rtk-query-endpoint-name'

export interface Meta {
  eTag: string | null
  resumeKey: string | null
}
export interface ExtraOptions {
  skipAuthorization?: boolean
}

/**
 * Helper to cache the query spec
 * @note The Service Worker must broadcast changes with the endpoint name and cache key
 * for the Redux store to update. We handle this in `sw-sync.ts`.
 */
export const createCachedQuerySpecHelper =
  (apiName: string) =>
  <T, S extends FetchArgs>(endpointName: string, callback: (queryArgs: T) => S) =>
  (queryArgs: T) => {
    const args = callback(queryArgs)
    return {
      ...args,
      headers: {
        [RTK_QUERY_API_NAME_HEADER]: apiName,
        [RTK_QUERY_ENDPOINT_NAME_HEADER]: endpointName,
        // We cannot use arbitrary data in the headers, so we'll Base64 encode it to be safe
        [RTK_QUERY_ARGS_HEADER]: encodeURIComponent(JSON.stringify(queryArgs)),
        ...args.headers,
      },
    } satisfies S
  }

/** Base Query extensions */

const createBaseQuery = ({ baseUrl, extraOptions }: { baseUrl: string; extraOptions: ExtraOptions }) =>
  fetchBaseQuery({
    prepareHeaders: (headers, api) => {
      const { getState, forced } = api

      // When forced, i.e., the RTK Query Cache is invalidated,
      // we set no-cache to avoid Service Worker cache and force network fetch
      // if we don't do this, the SW may return a cached response, and we get a "flicker" in the UI
      // where stale data is shown before the new data arrives.

      // @todo consider using a custom header instead to avoid interfering with other caches
      // which may e.g., return 304 Not Modified responses.
      // however, x-force-network requires SW support and also changes to CORS.
      if (forced) {
        headers.set('Cache-Control', 'no-cache')
      }

      /** Set authorization header if not set  */
      if (extraOptions?.skipAuthorization) {
        return headers
      }
      /** Set the authorization header */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const token = (getState() as any).user.authData?.idToken.jwtToken

      // In test, we don't enforce the token to be set
      if (!isTest()) {
        invariant(token, 'No token found')
      }

      headers.set('Authorization', `Bearer ${token}`)

      return headers
    },
    baseUrl,
  })

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions,
  Meta & FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  // wait until we're not in the login process
  await loginProcessMutex.waitForUnlock()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state = api.getState() as any
  const baseUrl = state.config.apiBaseUrl

  const baseQuery: ReturnType<typeof createBaseQuery> = retry(createBaseQuery({ baseUrl, extraOptions }), {
    maxRetries: MAX_RETRIES,
  })

  let result = await baseQuery(args, api, extraOptions)

  if (result.error?.status === 401) {
    // checking whether the mutex is locked.
    if (!loginProcessMutex.isLocked()) {
      const release = await loginProcessMutex.acquire()
      try {
        /**
         * We have two possible conditions here. We have a valid session, or we do not.
         * If we have a valid session, we should not retry as this is an item we don't have access to.
         * If we don't have a valid session, retry the request after refreshing the session.
         */

        /** We require valid Authdata in Redux  */
        const validSession = Boolean(state.user.authData) && !OAuth.isSessionExpired(state.user.authData)
        if (validSession) {
          /**
           * Throws the error - @see https://github.com/reduxjs/redux-toolkit/issues/3789
           * This is a bug in Redux Toolkit.
           * So for clients, they will receive the error, but as {error: {error}}
           */
          retry.fail(result.error)
        }
        /** We don't have a valid session, so get one */
        const sess = await getSessionForReauth()
        if (sess === transientRefreshFailure) {
          /** GH #1469: a retryable blip — keep the session and surface the original error. */
        } else if (sess?.idToken.payload.sub) {
          api.dispatch(userSlice.actions.setAuth(sess))
          // retry the initial query
          result = await baseQuery(args, api, extraOptions)
        } else {
          /**
           * No session, and no retry can produce one. Discard the dead session
           * so `OAuth.hasSession()` stops reporting a refreshable session, and
           * let AuthGuard drive re-authentication once `signout()` flips
           * `isLoggedIn`.
           *
           * NOT `OAuth.logout()`: that clears no local storage and instead
           * navigates to the logout proxy, tearing down the app mid-query and
           * leaving the dead tokens in place (the user then loops on a session
           * that can never refresh).
           */
          OAuth.clearSession()
          api.dispatch(userSlice.actions.signout())
        }
      } finally {
        // release must be called once the mutex should be released again.
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await loginProcessMutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }
  if (result.error) {
    const status = result.error.status

    /** Aborted fetch calls — don't report to networkBus; aborts aren't network failures */
    if (status === 'FETCH_ERROR' && result.error.error.startsWith('AbortError')) {
      return result as never
    }

    /** 404 are occuring during uploads, so we don't flag errors on those */
    if (result.error.status === 404) {
      return result as never
    }

    logger.error('Error loading data: ', result)

    /** Report real failures to networkBus so NetworkStatusProvider can track
     *  rolling-window failure counts for the 'degraded' transition. */
    networkBus.emit('rtk-query-result', { ok: false })

    if (typeof status === 'number' && status >= 500) {
      const alert = isOnline()
        ? ({
            message: `Failed to load data: ${JSON.stringify(result.error)}`,
            severity: 'error',
            actions: undefined,
          } satisfies Alert)
        : ({ message: 'Offline', severity: 'warning', actions: undefined } satisfies Alert)
      api.dispatch(notificationsSlice.actions.setAlert(alert))
    }
  } else {
    /** Successful response — clears the rolling failure window and stamps lastContactMs */
    networkBus.emit('rtk-query-result', { ok: true })
  }

  /** @note these must be declared in Access-Control-Expose-Headers, see tuna-api.ts */
  const eTag = result.meta?.response?.headers.get('etag') ?? null
  const resumeKey = result.meta?.response?.headers.get('x-resume-key') ?? null
  return {
    ...result,
    meta: result.meta && ({ ...result.meta, eTag, resumeKey } as never),
  }
}

export default baseQueryWithReauth
